// KPI Builder — runs the visualization being edited against the real data, a moment after the admin stops changing it.
import { useEffect, useRef, useState } from 'react';
import { useVisualizationService } from 'services';
import { PreviewRequest, PreviewResponse } from 'helpers/kpiBuilder/previewRequest';

const DEBOUNCE_MS = 600;

export interface LivePreviewState {
  loading: boolean;
  error: string;
  response: PreviewResponse | null;
}

const IDLE: LivePreviewState = { loading: false, error: '', response: null };

const messageOf = (e: unknown): string => (e instanceof Error ? e.message : String(e));

/** null request: nothing to run yet. A newer request always wins over an older one that is still in flight. */
export function useLivePreview(request: PreviewRequest | null): LivePreviewState {
  const service = useVisualizationService();
  const serviceRef = useRef(service);
  serviceRef.current = service;
  const latest = useRef(0);
  const [state, setState] = useState<LivePreviewState>(IDLE);
  const key = request ? JSON.stringify(request) : '';

  useEffect(() => {
    const id = ++latest.current;
    if (!key) {
      setState(IDLE);
      return undefined;
    }
    setState((s) => ({ ...s, loading: true, error: '' }));
    const timer = setTimeout(() => {
      serviceRef.current.previewVisualization(JSON.parse(key) as PreviewRequest).then(
        (response) => id === latest.current && setState({ loading: false, error: '', response }),
        (e: unknown) => id === latest.current && setState({ loading: false, error: messageOf(e), response: null })
      );
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [key]);

  return state;
}
